import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import { ReportingMapSelector } from 'teacher-ui-client/components/reporting/map-selector'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { t } from 'ember-intl'
import { array } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { not } from 'ember-truth-helpers'
import type ReStudentSummaryCourseProgressReadingRoute from 'teacher-ui-client/reporting/ui-scope/re/student-summary/course-progress/reading/route'
import type ReStudentSummaryCourseProgressReadingController from 'teacher-ui-client/reporting/ui-scope/re/student-summary/course-progress/reading/controller'
import RouteTemplate from 'ember-route-template'
import joinList from 'teacher-ui-client/helpers/join-list'
import Component from '@glimmer/component'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { service } from '@ember/service'
import { format } from 'date-fns'
import type { IntlService } from 'ember-intl'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { isPresent } from '@ember/utils'
import { uniq } from 'lodash'

export interface MapData {
  created_at: number
  correct_count: number
  map_number: number
  total_count: number
  attempts: number
  categories: {
    name: string
    areas: { correct: string[]; incorrect: string[]; name: string }[]
  }[]
  latest_passed_quiz: Nullable<{
    created_at: number
    correct_count: number
    total_count: number
  }>
}

interface Signature {
  Args: {
    model: ModelFor<ReStudentSummaryCourseProgressReadingRoute>
    controller: ReStudentSummaryCourseProgressReadingController
  }
}

export class ReStudentSummaryCourseProgressReadingPage extends Component<Signature> {
  @service intl!: IntlService

  @service lessonInfo!: LessonInfoService

  get currentMapData(): MapData | undefined {
    const { currentMapNumber } = this.args.controller
    const maps = this.args.model.readingMapDetails
    return maps?.find(({ map_number: mapNumber }: { map_number: number }) => {
      return mapNumber === currentMapNumber
    })
  }

  get latestPassedQuizFormattedDate(): string | undefined {
    if (!this.currentMapData?.latest_passed_quiz?.created_at) return

    const createdAt = new Date(this.currentMapData.latest_passed_quiz.created_at * 1000)
    const dateFormat = this.intl.t('dateFormats.dateFns.dd MMM')
    return format(createdAt, dateFormat)
  }

  get firstMapHavingData() {
    const [firstMap] = this.mapNumbersHavingData
    return firstMap
  }

  get mapNumbersHavingData() {
    const mapsData = this.args.model.readingMapDetails ?? []
    return mapsData.map(({ map_number: mapNumber }: { map_number: number }) => {
      return mapNumber
    })
  }

  get mapsAndPresenceForLinks() {
    const allMapNumbers = uniq(
      this.lessonInfo
        .lessonsFor(Precinct.RE_READING)
        .map((l) => l.map)
        .filter(isPresent),
    )
    const { mapNumbersHavingData } = this
    const mapObjects = allMapNumbers.map((mapNumber) => {
      const hasData = mapNumbersHavingData.includes(mapNumber)
      return { mapNumber: mapNumber, hasData }
    })
    return mapObjects
  }

  <template>
    <ReportingTeacherUiHeader
      @title={{t "reporting.title.studentDetails.courseProgressReading" student=@model.student.fullName}}
      @showBack={{true}}
    />

    <ReportingDetailedControls
      @students={{@model.students}}
      @exportOptions={{array ExportType.print}}
      @isDataPresent={{Boolean @model.readingMapDetails}}
      @studentRouteName="reporting.ui-scope.re.student-summary.course-progress.reading"
    />

    <ReportingNoDataMessageIf @on={{not @model.readingMapDetails}} class="relative">
      <ReportingMapSelector
        @itemReferences={{this.mapsAndPresenceForLinks}}
        @currentItemId={{@controller.currentMapNumber}}
        @title="Map:"
        @changeItem={{@controller.changeMap}}
      />
      <ContentTile data-test-course-progress-student-details-panel>
        <h3 data-test-student-details-header class="border-oceany-blue-150 border-b-4 p-4 print:border-black">
          {{t "reporting.mapWithOrdinal" number=@controller.currentMapNumber}}
        </h3>
        <div
          data-test-student-details-header-data
          class="border-oceany-blue-150 flex justify-end border-b-4 print:border-black"
        >
          <h5 class="m-4 inline-block">
            {{#if this.latestPassedQuizFormattedDate}}
              {{t "reporting.date"}}
              :
              {{this.latestPassedQuizFormattedDate}}
              *
            {{/if}}
            <Tooltip @text={{t "reporting.readingProgressPassConditionMessage"}} />
          </h5>
          <h5 class="m-4 inline-block">
            {{t "reporting.correct"}}
            :
            {{this.currentMapData.correct_count}}
            /
            {{this.currentMapData.total_count}}
          </h5>
          <h5 data-test-attempts-count class="m-4 inline-block">
            {{t "reporting.attempts"}}
            :
            {{this.currentMapData.attempts}}
          </h5>
        </div>
        <table data-test-student-details-table class="w-full" exportable>
          <thead>
            <tr class="bg-oceany-blue-100 border-b border-white">
              <th class="p-4"></th>
              <th class="p-4 text-left">
                {{t "reporting.correctAnswers"}}
              </th>
              <th class="p-4 text-left">
                {{t "reporting.incorrectAnswers"}}
              </th>
            </tr>
          </thead>
          {{! Multiple tbodys allows the tailwind even classes to make sense }}
          {{#each this.currentMapData.categories as |category|}}
            <tbody>
              <tr data-test-category-row class="bg-oceany-blue-50 hover:bg-oceany-blue-100 even:bg-white">
                <td class="w-1/3 p-4 font-bold" colspan="3">
                  {{category.name}}
                </td>
              </tr>
              {{#each category.areas as |area|}}
                <tr class="bg-oceany-blue-50 hover:bg-oceany-blue-100 even:bg-white">
                  <td class="w-1/3 p-4">
                    {{area.name}}
                  </td>
                  <td class="w-1/3 p-4">
                    {{joinList area.correct ", "}}
                  </td>
                  <td class="w-1/3 p-4">
                    {{joinList area.incorrect ", "}}
                  </td>
                </tr>
              {{/each}}
            </tbody>
          {{/each}}
        </table>
      </ContentTile>
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(ReStudentSummaryCourseProgressReadingPage)
